import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {getMipLevelSize, HalImage, HalImageDataType, HalImageRef} from "@cm/hal"
import {Definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blend-by-laplacian-pyramid/definition"
import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {SmoothingMode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/mipmap-helper-hal"
import {Box2, Box2Like, Size2, Size2Like, Vector2, Vector2Like} from "@cm/math"
import {InterpolationMode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/hal/resize-helper-hal"
import {HalPainterImageCompositorRef} from "@cm/hal/hal-ref"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {UPDATE_COMPLEXITY_HIGH} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

type Definition = typeof Definition

export class ImageOpBlendByLaplacianPyramidHal extends ImageOpHal<Definition, ImageOpBlendByLaplacianPyramidHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpBlendByLaplacianPyramidHalData>): ImageOpBlendByLaplacianPyramidHalData {
        return new ImageOpBlendByLaplacianPyramidHalData({
            ...args,
            descriptor: args.parameters.backgroundImage.descriptor,
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: UPDATE_COMPLEXITY_HIGH,
        })
    }
}

class ImageOpBlendByLaplacianPyramidHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpBlendByLaplacianPyramidHalData>) {
        super(args)
        this.mipMapHalo = this.ctx.mipmapHelper.getHaloWidth(this.mipmapSmoothing)
        this.interpolationMode = "cubic"
        this.interpolationHalo = this.ctx.resizeHelper.getHaloWidth(this.interpolationMode)
        this.composeLevel =
            args.prevData?.composeLevel.addRef() ??
            this.ctx.createPainter(
                "compositor",
                `
            uniform ivec2 u_targetOffset;
            
            vec4 computeColor(ivec2 targetPixel) {
                ivec2 sourcePixel = targetPixel - u_targetOffset;
                vec4 backgroundCoarse = texelFetch0(sourcePixel);
                vec4 backgroundFine = texelFetch1(sourcePixel);
                vec4 foregroundCoarse = texelFetch2(sourcePixel);
                vec4 foregroundFine = texelFetch3(sourcePixel);
                vec4 backgroundDifference = backgroundFine - backgroundCoarse;
                vec4 foregroundDifference = foregroundFine - foregroundCoarse;
                float mask = texelFetch4(sourcePixel).r;
                vec4 blendedDifference = mix(backgroundDifference, foregroundDifference, mask);
                vec4 prevResult = texelFetch5(sourcePixel);
                vec4 result = prevResult + blendedDifference;
                return result;
            }
        `,
            )
    }

    protected override dispose() {
        this.composeLevel.release()
        super.dispose()
    }

    protected override async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {backgroundImage, foregroundImage, maskImage, wrapAround, testParam1, testParam2} = this.parameters
        backgroundImage.addRef("BlendByLaplacianPyramid.writeRegion - backgroundImage")
        foregroundImage.addRef("BlendByLaplacianPyramid.writeRegion - foregroundImage")
        maskImage.addRef("BlendByLaplacianPyramid.writeRegion - maskImage")
        let backgroundGaussianPyramid: PyramidLevel[] = []
        let foregroundGaussianPyramid: PyramidLevel[] = []
        let maskGaussianPyramid: PyramidLevel[] = []
        try {
            backgroundGaussianPyramid = await this.buildGaussianPyramid(backgroundImage, mipLevelRegion, options)
            foregroundGaussianPyramid = await this.buildGaussianPyramid(foregroundImage, mipLevelRegion, options)
            maskGaussianPyramid = await this.buildGaussianPyramid(maskImage, mipLevelRegion, options)

            let showOnlyLevel: number | undefined = undefined
            let showType: "blended-level" | "background-only" | "foreground-only" | "mask" | undefined = undefined
            let placeholderBlack: HalImageRef | undefined = undefined
            let placeholderWhite: HalImageRef | undefined = undefined
            switch (Math.round((testParam2 ?? 0) * 4)) {
                default:
                case 0:
                    break
                case 1:
                    showType = "blended-level"
                    break
                case 2:
                    showType = "background-only"
                    break
                case 3:
                    showType = "foreground-only"
                    break
                case 4:
                    showType = "mask"
                    placeholderBlack = this.ctx.createImagePhysical(backgroundGaussianPyramid[0].image.ref.descriptor)
                    placeholderBlack.ref.clear({color: {r: 0, g: 0, b: 0, a: 1}})
                    placeholderWhite = this.ctx.createImagePhysical(backgroundGaussianPyramid[0].image.ref.descriptor)
                    placeholderWhite.ref.clear({color: {r: 1, g: 1, b: 1, a: 1}})
                    break
            }
            if (showType) {
                showOnlyLevel = Math.max(0, Math.min(backgroundGaussianPyramid.length - 1, Math.round((testParam1 ?? 0) * backgroundGaussianPyramid.length)))
            }

            const addressMode = wrapAround ? "wrap" : "clamp"
            let result = this.ctx.createImagePhysical(backgroundGaussianPyramid[0].image.ref.descriptor)
            let resultPrev = this.ctx.createImagePhysical(backgroundGaussianPyramid[0].image.ref.descriptor)
            const start = performance.now()
            for (let currMipLevel = backgroundGaussianPyramid.length - 1; currMipLevel >= 0; currMipLevel--) {
                const temp = result
                result = resultPrev
                resultPrev = temp
                const fineMaskGaussian = maskGaussianPyramid[currMipLevel]
                let fineBackgroundGaussian: PyramidLevel | undefined = backgroundGaussianPyramid[currMipLevel]
                let fineForegroundGaussian: PyramidLevel | undefined = foregroundGaussianPyramid[currMipLevel]
                const fineImageSize = fineBackgroundGaussian.image.ref.descriptor
                let coarseResult: HalImageRef | undefined = undefined
                let coarseBackgroundGaussian: HalImage | undefined = undefined
                let coarseForegroundGaussian: HalImage | undefined = undefined
                let coarseSize: Size2Like = {width: 1, height: 1}
                let coarseOffset: Vector2Like = {x: 0, y: 0}
                if (currMipLevel < backgroundGaussianPyramid.length - 1) {
                    const coarseBackgroundGaussianPyramidLevel = backgroundGaussianPyramid[currMipLevel + 1]
                    const coarseForegroundGaussianPyramidLevel = foregroundGaussianPyramid[currMipLevel + 1]
                    coarseSize = coarseBackgroundGaussianPyramidLevel.size
                    coarseOffset = coarseBackgroundGaussianPyramidLevel.offset
                    const subRegionSize = Size2.min(resultPrev.ref.descriptor, coarseBackgroundGaussianPyramidLevel.image.ref.descriptor)
                    coarseResult = this.ctx.createImageView({
                        image: resultPrev,
                        type: "sub-image",
                        region: {...subRegionSize, x: 0, y: 0},
                    }) // TODO investigate why this doesn't work with proper checks (seems to be a bug !)
                    coarseBackgroundGaussian = coarseBackgroundGaussianPyramidLevel.image.ref
                    coarseForegroundGaussian = coarseForegroundGaussianPyramidLevel.image.ref
                }
                const isLast = currMipLevel === 0
                const nextResult: HalImage = isLast ? outputImage : result.ref
                const targetOffset: Vector2Like = isLast ? mipLevelRegion : {x: 0, y: 0}
                const targetMipLevel = isLast ? mipLevelRegion.mipLevel : 0
                const targetRegion: Box2Like = isLast ? mipLevelRegion : {...fineImageSize, x: 0, y: 0}
                if (showOnlyLevel !== undefined) {
                    coarseBackgroundGaussian = undefined
                    coarseForegroundGaussian = undefined
                    if (currMipLevel !== showOnlyLevel) {
                        fineBackgroundGaussian = undefined
                        fineForegroundGaussian = undefined
                    } else {
                        switch (showType) {
                            case "blended-level":
                                break
                            case "background-only":
                                fineForegroundGaussian = undefined
                                break
                            case "foreground-only":
                                fineBackgroundGaussian = undefined
                                break
                            case "mask":
                                if (!placeholderBlack || !placeholderWhite) {
                                    throw new Error("Placeholder image is not set")
                                }
                                fineBackgroundGaussian = {
                                    ...fineBackgroundGaussian,
                                    image: placeholderBlack,
                                }
                                fineForegroundGaussian = {
                                    ...fineForegroundGaussian,
                                    image: placeholderWhite,
                                }
                                break
                        }
                    }
                }
                let upsampledCoarseBackgroundGaussian: HalImageRef | undefined = undefined
                if (coarseBackgroundGaussian && fineBackgroundGaussian) {
                    upsampledCoarseBackgroundGaussian = this.ctx.createImagePhysical(fineBackgroundGaussian.image.ref.descriptor)
                    this.ctx.resizeHelper.resize({
                        source: coarseBackgroundGaussian,
                        sourceRegion: {
                            ...coarseOffset,
                            ...coarseSize,
                        },
                        target: upsampledCoarseBackgroundGaussian.ref,
                        targetRegion: {
                            ...fineBackgroundGaussian.offset,
                            ...fineBackgroundGaussian.size,
                        },
                        interpolationMode: this.interpolationMode,
                        addressMode: addressMode,
                    })
                }
                let upsampledCoarseForegroundGaussian: HalImageRef | undefined = undefined
                if (coarseForegroundGaussian && fineForegroundGaussian) {
                    upsampledCoarseForegroundGaussian = this.ctx.createImagePhysical(fineForegroundGaussian.image.ref.descriptor)
                    this.ctx.resizeHelper.resize({
                        source: coarseForegroundGaussian,
                        sourceRegion: {
                            ...coarseOffset,
                            ...coarseSize,
                        },
                        target: upsampledCoarseForegroundGaussian.ref,
                        targetRegion: {
                            ...fineForegroundGaussian.offset,
                            ...fineForegroundGaussian.size,
                        },
                        interpolationMode: this.interpolationMode,
                        addressMode: addressMode,
                    })
                }
                let upsampledCoarseResult: HalImageRef | undefined = undefined
                if (coarseResult && fineForegroundGaussian) {
                    upsampledCoarseResult = this.ctx.createImagePhysical(fineForegroundGaussian.image.ref.descriptor)
                    this.ctx.resizeHelper.resize({
                        source: coarseResult.ref,
                        sourceRegion: {
                            ...coarseOffset,
                            ...coarseSize,
                        },
                        target: upsampledCoarseResult.ref,
                        targetRegion: {
                            ...fineForegroundGaussian.offset,
                            ...fineForegroundGaussian.size,
                        },
                        interpolationMode: this.interpolationMode,
                        addressMode: addressMode,
                    })
                }
                this.composeLevel.ref.paint({
                    target: nextResult,
                    sourceImages: [
                        upsampledCoarseBackgroundGaussian?.ref,
                        fineBackgroundGaussian?.image.ref,
                        upsampledCoarseForegroundGaussian?.ref,
                        fineForegroundGaussian?.image.ref,
                        fineMaskGaussian.image.ref,
                        upsampledCoarseResult?.ref,
                    ],
                    parameters: {
                        u_targetOffset: {type: "int2", value: targetOffset},
                    },
                    options: {
                        targetMipLevel,
                        targetRegion,
                    },
                })
                upsampledCoarseBackgroundGaussian?.release()
                upsampledCoarseForegroundGaussian?.release()
                upsampledCoarseResult?.release()
                coarseResult?.release()
            }
            this.logger?.trace("Laplacian pyramid blend time: ", performance.now() - start)
            result.release()
            resultPrev.release()
            placeholderBlack?.release()
            placeholderWhite?.release()
        } finally {
            backgroundGaussianPyramid.forEach((level) => level.image.release())
            foregroundGaussianPyramid.forEach((level) => level.image.release())
            maskGaussianPyramid.forEach((level) => level.image.release())
            backgroundImage.release("BlendByLaplacianPyramid.writeRegion - backgroundImage")
            foregroundImage.release("BlendByLaplacianPyramid.writeRegion - foregroundImage")
            maskImage.release("BlendByLaplacianPyramid.writeRegion - maskImage")
        }
    }

    protected override onSourceRegionsInvalidated(_sourceImage: AnyImageOpHalData, _sourceRegions: Regions) {
        this.invalidateRegions() // we need to invalidate everything because the blending depends on the whole image
    }

    private getExpandedRegion(sourceImageSize: Size2Like, region: Box2Like, margin: number | Vector2Like, mipLevel?: number) {
        if (typeof margin === "number") {
            margin = new Vector2(margin, margin)
        }
        mipLevel ??= 0
        const mipLevelSize = Box2.fromSize(getMipLevelSize(sourceImageSize, mipLevel))
        const clippedRegion = Box2.intersect(region, mipLevelSize)
        const expandedRegion = Box2.expandByMargin(clippedRegion, margin)
        const clippedExpandedRegion = Box2.intersect(expandedRegion, mipLevelSize)
        return {
            region: clippedRegion,
            expandedRegion: clippedExpandedRegion,
        }
    }

    private extractSubImage(sourceImage: HalImageRef, region: Box2Like, mipLevel?: number) {
        mipLevel ??= 0
        // TODO currently we can not use a view because we release the RegionLockedHalImageRef after the call to extractSubImage (potentially freeing the referenced pages)
        const useView = false // TODO: we are faster not using a view - investigate !
        if (useView) {
            return this.ctx.createImageView({
                image: sourceImage,
                type: "sub-image",
                region: region,
                mipLevelOffset: mipLevel,
            })
        } else {
            const subImage = this.ctx.createImagePhysical(
                {
                    ...sourceImage.ref.descriptor,
                    width: region.width,
                    height: region.height,
                },
                {
                    dataType: this.processingDataType,
                },
            )
            this.ctx.blit({
                sourceImage: sourceImage.ref,
                sourceRegion: region,
                sourceMipLevel: mipLevel,
                targetImage: subImage.ref,
            })
            return subImage
        }
    }

    private async extractPyramidLevel(
        sourceImage: AnyImageOpHalData,
        region: Box2Like,
        margin: number | Vector2Like,
        mipLevel: number,
        options?: UpdateImageOptions,
    ): Promise<PyramidLevel> {
        const r = this.getExpandedRegion(sourceImage.descriptor, region, margin, mipLevel)
        const regionLockedHalImageRef = await sourceImage.getUpdatedHalImage({...r.expandedRegion, mipLevel}, options)
        const subImage = this.extractSubImage(regionLockedHalImageRef.halImageRef, r.expandedRegion, mipLevel)
        regionLockedHalImageRef.release()
        return {
            image: subImage,
            offset: {
                x: r.region.x - r.expandedRegion.x,
                y: r.region.y - r.expandedRegion.y,
            },
            size: {
                width: r.region.width,
                height: r.region.height,
            },
        }
    }

    private async buildGaussianPyramid(sourceImage: AnyImageOpHalData, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<PyramidLevel[]> {
        const start = performance.now()
        const minSize = Math.min(sourceImage.descriptor.width, sourceImage.descriptor.height)
        const lowerPow2Exp = Math.floor(Math.log2(minSize))
        const numMipLevels = Math.max(1, lowerPow2Exp - 2)
        let x0 = mipLevelRegion.x
        let y0 = mipLevelRegion.y
        let x1 = mipLevelRegion.x + mipLevelRegion.width
        let y1 = mipLevelRegion.y + mipLevelRegion.height
        const pendingBaseLevel = this.extractPyramidLevel(
            sourceImage,
            {
                x: x0,
                y: y0,
                width: x1 - x0,
                height: y1 - y0,
            },
            0,
            mipLevelRegion.mipLevel,
            options,
        )
        const baseLevel = await pendingBaseLevel
        // const pendingMipLevels: Promise<PyramidLevel>[] = [pendingBaseLevel] // TODO investigate why awaiting all at once causes artifacts !
        const mipLevels = [baseLevel]
        // generate pyramid levels from mip levels
        let offsetX0 = 0
        let offsetY0 = 0
        let offsetX1 = 0
        let offsetY1 = 0
        let nextSourceMipLevel = mipLevelRegion.mipLevel + 1
        while (nextSourceMipLevel < numMipLevels) {
            const nextX0 = Math.floor(x0 * 0.5)
            const nextY0 = Math.floor(y0 * 0.5)
            const nextX1 = Math.ceil(x1 * 0.5)
            const nextY1 = Math.ceil(y1 * 0.5)
            offsetX0 += x0 - nextX0 * 2
            offsetY0 += y0 - nextY0 * 2
            offsetX1 += x1 - nextX1 * 2
            offsetY1 += y1 - nextY1 * 2
            x0 = nextX0
            y0 = nextY0
            x1 = nextX1
            y1 = nextY1
            offsetX0 *= 0.5
            offsetY0 *= 0.5
            offsetX1 *= 0.5
            offsetY1 *= 0.5
            const createLevel = async (
                x0: number,
                y0: number,
                x1: number,
                y1: number,
                offsetX0: number,
                offsetY0: number,
                offsetX1: number,
                offsetY1: number,
            ): Promise<PyramidLevel> => {
                const nextLevel = await this.extractPyramidLevel(
                    sourceImage,
                    {
                        x: x0,
                        y: y0,
                        width: x1 - x0,
                        height: y1 - y0,
                    },
                    this.interpolationHalo,
                    nextSourceMipLevel,
                    options,
                )
                return {
                    image: nextLevel.image,
                    offset: {
                        x: nextLevel.offset.x + offsetX0,
                        y: nextLevel.offset.y + offsetY0,
                    },
                    size: {
                        width: nextLevel.size.width - (offsetX0 - offsetX1),
                        height: nextLevel.size.height - (offsetY0 - offsetY1),
                    },
                }
            }
            const pendingNextLevel = createLevel(x0, y0, x1, y1, offsetX0, offsetY0, offsetX1, offsetY1)
            const nextLevel = await pendingNextLevel
            mipLevels.push(nextLevel)
            // pendingMipLevels.push(pendingNextLevel)
            nextSourceMipLevel++
        }
        // const mipLevels = await Promise.all(pendingMipLevels)
        this.logger?.trace("Gaussian pyramid build time: ", performance.now() - start)
        return mipLevels
    }

    private readonly composeLevel: HalPainterImageCompositorRef

    private readonly mipmapSmoothing: SmoothingMode = "none" //"binomial6"
    private readonly mipMapHalo: number // "binomial6" has a halo of 2; Attention: current implementation needs this to be even !
    private readonly interpolationMode: InterpolationMode
    private readonly interpolationHalo: number
    private readonly processingDataType: HalImageDataType = "float16" // TODO is it acceptable to go lower than this ?
}

type PyramidLevel = {
    image: HalImageRef
    offset: Vector2Like
    size: Size2Like
}
